<template>
  <VDialogV3
    id="supply-offer-card"
    position="right"
    width="600px"
    :entity="localSupplyOffer"
    :loading="localSupplyOffer === undefined"
    :submit-disabled="isLoading"

    :tabs="tabs"
    v-model:active-tab="activeTab"

    @submit="onSubmit"
  >
    <!-- v-show, а не v-if: контролы должны остаться смонтированными,
         иначе вкладку не провалидировать -->
    <fieldset v-show="activeTab.id === 'main'" data-tab-id="main">
      <!-- Партнёр -->
      <section>
        <header>Партнер</header>

        <VSelectV2
          ref="seller"
          v-model="localSupplyOffer.seller"
          label="Поставщик"
          :data="({ pattern }) => PartnersService.searchSellers(pattern)"
          :disabled="localSupplyOffer.isExisting"
          required
        />

        <VSelectV2
          v-model="localSupplyOffer.sellerCompany"
          label="Юр. лицо"
          :data="sellerCompanySource"
          :disabled="localSupplyOffer.isExisting"
          required
        />
      </section>

      <hr>

      <!-- Товар -->
      <section>
        <header>Товар</header>

        <VSuggestionSelectV2
          v-model="localSupplyOffer.product"
          label="Товар"
          :data="({ pattern }) => ProductsService.search(pattern)"
          :disabled="localSupplyOffer.isExisting"
          required
          @update:modelValue="onProductInput"
        >
          <template v-if="localSupplyOffer.product?.code" #helper-text>
            Артикул: <span>{{ localSupplyOffer.product.code }}</span>
          </template>
        </VSuggestionSelectV2>

        <VInput
          v-model="localSupplyOffer.name"
          label="Наименование товара в ИС поставщика"
          layout="vertical"
          :max-length="175"
        />

        <VSelectV2
          v-model="localSupplyOffer.manufacturer"
          label="Производитель"
          :data="({ pattern }) => ManufacturersService.getList({ pattern })"
          :disabled="localSupplyOffer.isExisting"
          hide-clear-btn
          required
        />

        <VSuggestionSelectV2
          v-model="localSupplyOffer.tradeMark"
          label="Торговая марка"
          :data="({ pattern }) => TradeMarksService.getList({ pattern })"
        />

        <VSelectV2
          v-model="localSupplyOffer.statusAvailability"
          label="Статус доступности товара"
          :data="SupplyOffersService.getStatusesAvailability()"
        />

        <VSwitch
          v-model="localSupplyOffer.isMain"
          theme="slider"
          label="Основной или альтернативный"
          checked-label="Основной"
          unchecked-label="Альтернативный"
        />

        <VInputNumberRusV2
          v-model="localSupplyOffer.minOrderVolume"
          label="Минимальный объем заказа"
          :type="Qty"
        />

        <VInputNumberRus
          v-model="localSupplyOffer.deliveryDaysToStore"
          label="Дни поставки"
          layout="vertical"
          :max-length="4"
        >
          <template #helper-text>
            Укажите срок доставки поставщиком до его склада в днях
          </template>
        </VInputNumberRus>

        <VFormItem ratio="2/1">
          <VSelectV2
            v-model="localSupplyOffer.currency"
            label="Валюта"
            :data="CurrenciesService.currencies"
            :disabled="localSupplyOffer.isExisting"
            hide-clear-btn
            required
          />

          <VSelectV2
            v-model="localSupplyOffer.unit"
            label="Ед. измерения"
            :data="unitSource"
            :disabled="localSupplyOffer.product?.isExisting === true"
            hide-clear-btn
            required
          />
        </VFormItem>

        <VInputNumberRusV2
          v-model="localSupplyOffer.packageVolume"
          label="Фасовка"
          :type="Qty"
          :unit="localSupplyOffer.unit"
        />
      </section>

      <hr>

      <section>
        <VSwitch
          v-model="localSupplyOffer.isExcludedFromTenders"
          theme="slider"
          :checked-value="false"
          :unchecked-value="true"
          checked-label="Допущен к тендерам"
          unchecked-label="Исключен из тендеров"
        />
        <VHelperText
          v-if="localSupplyOffer.isExcludedFromTenders === true"
          icon="attention"
        >
          Обратите внимание, что при выборе опции <b>"Исключен из тендеров"</b> поставщик не сможет делать ставки в тендерах по этому товару и подавать заявки на участие в них.
        </VHelperText>
      </section>

      <hr>

      <!-- Статус разрешения -->
      <section>
        <label>Статус</label>
        <VButtonGroup>
          <VButton
            v-for="(status, key) in SupplyOffersService.getStatuses()"
            :key="key"
            :theme="getTheme(status)"
            @click="statusChange(status)"
          >
            {{ status.name }}
          </VButton>
        </VButtonGroup>

        <VFormItem
          class="supply-offer-related-url">
          <VInput
            v-model="localSupplyOffer.relatedUrl"
            label="Ссылка"
          >
            <template #helper-text>
              Ссылка на материал, относящиеся к данному разрешению на поставку, например на задачу Jira
            </template>
          </VInput>
          <VButton
            v-if="localSupplyOffer.relatedUrl"
            theme="link"
            title="Копировать ссылку"
            @click="$core.copyToClipboard(localSupplyOffer.relatedUrl)"
          >
            <template #icon>
              <VIcon material="content_copy" theme="outlined"/>
            </template>
          </VButton>
        </VFormItem>

      </section>

      <hr>

      <!-- Бюджетная цена -->
      <BudgetPriceForm
        ref="budgetPriceForm"
        v-model:budget-price="budgetPrice"
        :product="localSupplyOffer.product"
      />

      <SupplyOfferTariffsGrid
        :supply-offer="localSupplyOffer"
        :budget-price="budgetPrice"
        @tariff-created="onTariffCreated"
      />
    </fieldset>

    <fieldset v-if="activeTab.id === 'files'" data-tab-id="files">
      <FilesList :entities="filesEntities" />
    </fieldset>
  </VDialogV3>
</template>

<script lang="ts">

import SupplyOffer            from '@/domains/SupplyOffers/SupplyOffer';
import SupplyOffersService    from '@/domains/SupplyOffers/SupplyOffersService';
import SupplyOfferTariffsGrid from '@/domains/SupplyOffers/components/SupplyOfferCard/SupplyOfferTariffsGrid.vue';

import type BudgetPrice   from '@/domains/BudgetPrices/BudgetPrice';
import BudgetPriceForm from "@/domains/BudgetPrices/components/BudgetPriceCard/BudgetPriceForm.vue";
import CompaniesService    from '@/domains/Companies/CompaniesService';
import CurrenciesService   from '@/domains/Currencies/CurrenciesService';
import ManufacturersService from '@/domains/Manufacturers/ManufacturersService';
import PartnersService     from '@/domains/Partners/PartnersService';
import ProductsService     from '@/domains/Products/ProductsService';
import TradeMarksService   from '@/domains/TradeMarks/TradeMarksService';
import UnitsService        from '@/domains/Units/UnitsService';

import EntityPointer from '@/domains/EntityPointer/EntityPointer';

import Qty    from '@/core/types/Qty';

import type Product from '@/domains/Products/Product';

export default defineComponent({
  name: 'SupplyOfferCard',

  components: {
    SupplyOfferTariffsGrid,
    BudgetPriceForm,
  },

  props: {
    /** Существующее разрешение — карточка грузит его сама */
    id: {
      type:    Number,
      default: null,
    },
    /** Готовое разрешение: заготовка для создания либо строка реестра */
    supplyOffer: {
      type:    Object as PropType<SupplyOffer>,
      default: null,
    },
  },

  setup() {
    return {
      CurrenciesService,
      ManufacturersService,
      PartnersService,
      ProductsService,
      SupplyOffersService,
      TradeMarksService,
      UnitsService,
      Qty,
    };
  },

  data() {
    return {
      localSupplyOffer: undefined as unknown as SupplyOffer,

      // Цена, которой владеет BudgetPriceForm — здесь нужна только для сетки тарифов
      budgetPrice: undefined as unknown as BudgetPrice,

      isLoading: false,

      activeTab: { id: 'main', name: 'Основное' } as { id: string; name?: string },

      /** Юр. лица продавца: грузятся по выбранному seller, зависимый источник. */
      sellerCompanySource: this.$core.loader.select<SupplyOffer>(({ seller }) =>
        CompaniesService.getList({ partnerId: seller!.id })
      )
        .dependsOn({ seller: () => this.localSupplyOffer.seller})
        .preloadOnFocus(this.supplyOffer?.isExisting === true)
        .preloadOnMount(this.supplyOffer?.isExisting === false),

      /** Единицы измерения: список статичный, от товара зависит только подстановка его единицы. */
      unitSource: this.$core.loader.select<SupplyOffer>(UnitsService.units.toArray())
        .dependsOn({ product: () => this.localSupplyOffer?.product })
        .default((options, { product }) =>
          options.find(option => option.id === product?.unit?.id)
        ),
    };
  },

  computed: {
    /**
     * У нового разрешения вкладок нет: файлы цеплять не к чему,
     * пока не появился id.
     */
    tabs() {
      if (this.localSupplyOffer?.isExisting === true) {
        return [
          { id: 'main',  name: 'Основное' },
          { id: 'files', name: 'Файлы'    },
        ];
      }
      else {
        return null;
      }
    },

    filesEntities(): EntityPointer[] {
      if (this.localSupplyOffer?.isExisting === true) {
        return [new EntityPointer('supplyOffer', this.localSupplyOffer.id, this.localSupplyOffer.toString())];
      }
      else {
        return [];
      }
    },
  },

  async created() {
    await this.init();
  },

  async mounted() {
    /** Сущность так и не появилась (проп не передан / init не отработал) — фокусить нечего */
    if (this.localSupplyOffer === undefined) {
      return;
    }

    /** из-за того что v-show в табах и это нужно для vform, выползает попытка обратится к ref="seller" в момент создания */
    if (this.$refs.seller === undefined) {
      return;
    }

    /** Если это редактирование, то фокус не нужен */
    if (this.localSupplyOffer.isExisting === true) {
      return;
    }

    await this.$nextTick();

    const input = this.$refs.seller.$el.querySelector('input');

    if (input) {
      input.focus();
    }
  },

  methods: {
    /** Инициализация карточки */
    async init(): Promise<void> {
      /** Разрешение: по id загружаем с бека, если пришел объект с id — все равно грузим с бэка */
      if (this.id) {
        this.localSupplyOffer = await SupplyOffersService.get(this.id);
      }
      else if (this.supplyOffer?.id) {
        this.localSupplyOffer = await SupplyOffersService.get(this.supplyOffer.id);
      }
      else if (this.$core.utils.isNullOrUndefined(this.supplyOffer) === false) {
        this.localSupplyOffer = this.supplyOffer;
      }
    },

    /** Статус может быть не проставлен — тогда ни одна кнопка не подсвечена */
    getTheme(status) {
      if (status.id === this.localSupplyOffer.status?.id) {
        return 'primary';
      }
      else {
        return 'default';
      }
    },

    statusChange(status) {
      this.localSupplyOffer.status = status;
    },

    onProductInput(product: Product) {
      this.$refs.budgetPriceForm.load(product);
    },

    async onTariffCreated() {
      if (this.localSupplyOffer.isExisting) {
        const copy = this.$core.utils.cloneDeep(this.localSupplyOffer);
        await SupplyOffersService.update(copy);
        this.localSupplyOffer = await SupplyOffersService.get(copy.id);
      }
    },

    /**
     * Зовётся из @submit диалога — VDialogV3 эмитит его только после
     * успешной валидации своей VForm, ручная валидация не нужна.
     *
     * VDialogV3 не закрывается сам после @submit — закрываем вручную после успеха.
     */
    async onSubmit(): Promise<void> {
      this.isLoading = true;

      let success: boolean;

      if (this.localSupplyOffer.isExisting) {
        success = await SupplyOffersService.update(this.localSupplyOffer);
      }
      else {
        const created = await SupplyOffersService.add(this.localSupplyOffer);
        success = created !== null;
      }

      this.isLoading = false;

      if (success) {
        await this.$refs.budgetPriceForm.submit();
        this.$core.dialogs.SupplyOfferCard.resolve();
      }
    },
  },
});
</script>
